import React, { useRef, useEffect, useState, useCallback } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { supabase } from '@/lib/supabase';
import { Laptop } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import {
  CameraOff,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Search,
  QrCode,
} from 'lucide-react';

interface LaptopQrScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLaptopScanned: (laptop: Laptop) => void;
  alreadyInCart: (laptopId: string) => boolean;
}

export const LaptopQrScannerModal: React.FC<LaptopQrScannerModalProps> = ({
  isOpen,
  onClose,
  onLaptopScanned,
  alreadyInCart,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReader = useRef<BrowserMultiFormatReader | null>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);

  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [scanFeedback, setScanFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [manualInput, setManualInput] = useState('');

  const stopCamera = useCallback(() => {
    if (controlsRef.current) {
      try {
        controlsRef.current.stop();
      } catch (e) {
        console.error('Error stopping camera:', e);
      }
      controlsRef.current = null;
    }
    setScanning(false);
  }, []);

  const resolveLaptopPayload = useCallback(
    async (rawCode: string) => {
      const code = rawCode.trim();
      if (!code) return;

      setIsResolving(true);
      setScanFeedback(null);

      try {
        let laptop: Laptop | null = null;

        // Pattern 1: AKT QR payload AKT1:L:<uuid>
        if (code.startsWith('AKT1:L:')) {
          const uuid = code.replace('AKT1:L:', '').trim();
          const { data, error } = await supabase
            .from('laptops')
            .select('*')
            .eq('id', uuid)
            .maybeSingle();
          if (error) throw error;
          laptop = data;
        }

        // Pattern 2: Stock ID LAP-XXXXXX
        if (!laptop && /^LAP-\d+/i.test(code)) {
          const { data, error } = await supabase
            .from('laptops')
            .select('*')
            .ilike('stock_id', code)
            .maybeSingle();
          if (error) throw error;
          laptop = data;
        }

        // Pattern 3: Serial number, barcode, or general match
        if (!laptop) {
          const { data, error } = await supabase
            .from('laptops')
            .select('*')
            .or(`stock_id.ilike.${code},serial_number.ilike.${code},barcode.ilike.${code},service_tag.ilike.${code}`)
            .limit(1)
            .maybeSingle();
          if (error) throw error;
          laptop = data;
        }

        if (!laptop) {
          setScanFeedback({
            type: 'error',
            message: `No laptop found matching "${code}". Please check the Stock ID or QR code.`,
          });
          return;
        }

        // Check already in cart
        if (alreadyInCart(laptop.id)) {
          setScanFeedback({
            type: 'error',
            message: `Laptop ${laptop.stock_id} (${laptop.brand} ${laptop.model}) is already in the sale cart.`,
          });
          return;
        }

        // Check availability
        if (laptop.status !== 'in_stock') {
          setScanFeedback({
            type: 'error',
            message: `Laptop ${laptop.stock_id} cannot be added. Current status is "${laptop.status}". Only in-stock units can be sold.`,
          });
          return;
        }

        if (laptop.archived_at) {
          setScanFeedback({
            type: 'error',
            message: `Laptop ${laptop.stock_id} is an archived record.`,
          });
          return;
        }

        // Successfully resolved and available!
        setScanFeedback({
          type: 'success',
          message: `Added ${laptop.stock_id} — ${laptop.brand} ${laptop.model} to cart!`,
        });

        onLaptopScanned(laptop);

        // Auto close after brief success indication
        setTimeout(() => {
          onClose();
        }, 700);
      } catch (err: any) {
        setScanFeedback({
          type: 'error',
          message: err?.message || 'Error resolving laptop details.',
        });
      } finally {
        setIsResolving(false);
      }
    },
    [alreadyInCart, onLaptopScanned, onClose]
  );

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setScanFeedback(null);

    try {
      codeReader.current = new BrowserMultiFormatReader();
      const videoElement = videoRef.current;
      if (!videoElement) return;

      setScanning(true);

      const controls = await codeReader.current.decodeFromVideoDevice(
        undefined,
        videoElement,
        (result) => {
          if (result) {
            const text = result.getText();
            stopCamera();
            void resolveLaptopPayload(text);
          }
        }
      );
      controlsRef.current = controls;
    } catch (err: any) {
      setScanning(false);
      setCameraError(
        'Could not access camera. Please allow camera permissions or enter the Stock ID / Serial manually below.'
      );
    }
  }, [resolveLaptopPayload, stopCamera]);

  useEffect(() => {
    if (isOpen) {
      void startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, startCamera, stopCamera]);

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualInput.trim()) {
      void resolveLaptopPayload(manualInput.trim());
      setManualInput('');
    }
  };

  return (
    <Modal isOpen={isOpen} title="Scan Laptop QR into Cart" onClose={onClose}>
      <div className="space-y-4 text-xs">
        {/* Camera Viewfinder */}
        <div className="relative bg-black rounded-lg overflow-hidden aspect-video flex items-center justify-center border border-brand-border">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
          />

          {/* Scanner Overlay Box */}
          {scanning && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-48 border-2 border-brand-green/80 rounded-lg relative flex items-center justify-center">
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-brand-green" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-brand-green" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-brand-green" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-brand-green" />
                <div className="w-full h-0.5 bg-brand-green/70 animate-pulse" />
              </div>
            </div>
          )}

          {!scanning && !cameraError && (
            <div className="absolute inset-0 bg-brand-black/60 flex flex-col items-center justify-center text-white space-y-2">
              <QrCode className="w-10 h-10 text-brand-green animate-pulse" />
              <p className="font-semibold text-xs">Initializing camera scanner…</p>
            </div>
          )}

          {cameraError && (
            <div className="absolute inset-0 bg-brand-bg p-4 flex flex-col items-center justify-center text-center space-y-2">
              <CameraOff className="w-8 h-8 text-brand-red" />
              <p className="font-bold text-brand-black text-xs">Camera Unavailable</p>
              <p className="text-brand-muted text-[11px] max-w-xs">{cameraError}</p>
              <button
                type="button"
                onClick={startCamera}
                className="px-3 py-1.5 bg-brand-green text-white text-xs font-bold rounded-md hover:bg-brand-green-hover"
              >
                Retry Camera
              </button>
            </div>
          )}
        </div>

        {/* Feedback Alert */}
        {isResolving && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center space-x-2 text-blue-900">
            <Loader2 className="w-4 h-4 animate-spin text-blue-600 shrink-0" />
            <span className="font-semibold">Resolving laptop from inventory…</span>
          </div>
        )}

        {scanFeedback && (
          <div
            className={`p-3 rounded-lg border flex items-start space-x-2 ${
              scanFeedback.type === 'success'
                ? 'bg-green-50 border-green-300 text-green-900'
                : 'bg-red-50 border-red-300 text-red-900'
            }`}
          >
            {scanFeedback.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />
            )}
            <div className="flex-1 font-semibold">{scanFeedback.message}</div>
          </div>
        )}

        {/* Manual Input Fallback */}
        <form onSubmit={handleManualSubmit} className="pt-2 border-t border-brand-border space-y-2">
          <label className="block font-bold text-brand-black text-[11px] uppercase tracking-wider">
            Or Enter Stock ID / Serial Manually
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              placeholder="e.g. LAP-000054, SN12345678, or paste AKT1:L:..."
              className="flex-1 px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-xs text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green font-mono"
            />
            <button
              type="submit"
              disabled={isResolving || !manualInput.trim()}
              className="px-4 py-2 bg-brand-green text-white font-bold text-xs rounded-md hover:bg-brand-green-hover disabled:opacity-50 transition-colors flex items-center space-x-1"
            >
              <Search className="w-3.5 h-3.5" />
              <span>Lookup</span>
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
